/**
 * Skill Setup Card
 *
 * Pending install card for Losi Skills.
 *
 * Primary responsibilities:
 * - Render skill install/setup results from Integrations and chat tools
 * - Parse setup payloads from skill search, name, or GitHub URL flows
 *
 * Dependencies:
 * - Unified integrations API
 * - Sonner toast notifications
 */

'use client'

import { BookOpenCheck, ExternalLink, Github, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export type SkillSetupCard = {
  actionLabel: string
  actionUrl: string | null
  cardState: string
  description: string
  enableUrl: string | null
  githubUrl: string | null
  installUrl: string | null
  integrationId: string | null
  requestedName: string
  title: string
  warnings: string[]
}

interface SkillSetupCardViewProps {
  className?: string
  compact?: boolean
  setup: SkillSetupCard
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function getString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function getStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.flatMap((item) => (typeof item === 'string' && item.trim() ? [item.trim()] : []))
    : []
}

function getNestedAction(value: Record<string, unknown>): Record<string, unknown> {
  return isRecord(value.action) ? value.action : {}
}

function isReviewState(value: string) {
  const normalized = value.trim().toLowerCase().replace(/[_-]+/g, ' ')

  return normalized === 'needs review' || normalized === 'pending review'
}

function hasSkillSignal(value: Record<string, unknown>, setup: Record<string, unknown>) {
  const kind = `${getString(value.kind) ?? ''} ${getString(setup.kind) ?? ''}`.toLowerCase()

  return (
    kind.includes('skill') ||
    isRecord(value.skillSetup) ||
    isRecord(value.skill_setup) ||
    Boolean(
      setup.integrationId ||
        setup.integration_id ||
        setup.skillId ||
        setup.skill_id ||
        setup.skillName ||
        setup.skill_name ||
        setup.skillPath ||
        setup.skill_path ||
        setup.sourceType ||
        setup.source_type ||
        setup.githubUrl ||
        setup.github_url ||
        setup.repositoryUrl ||
        setup.repository_url
    )
  )
}

export function getSkillSetupCard(value: unknown): SkillSetupCard | null {
  if (!isRecord(value)) {
    return null
  }

  const rawSetup = isRecord(value.skillSetup)
    ? value.skillSetup
    : isRecord(value.skill_setup)
      ? value.skill_setup
      : isRecord(value.setupCard)
        ? value.setupCard
        : isRecord(value.setup_card)
          ? value.setup_card
          : value
  const setup = isRecord(rawSetup.setup_card) ? rawSetup.setup_card : rawSetup

  if (!hasSkillSignal(value, setup)) {
    return null
  }

  // Ready/create results without an install payload should not render a setup card.
  const hasExplicitSetup =
    isRecord(value.skillSetup) ||
    isRecord(value.skill_setup) ||
    isRecord(value.setupCard) ||
    isRecord(value.setup_card)
  const kindBlobEarly =
    `${getString(value.kind) ?? ''} ${getString(setup.kind) ?? ''}`.toLowerCase()
  if (
    !hasExplicitSetup &&
    (kindBlobEarly.includes('skill_ready') || kindBlobEarly.includes('skill_create'))
  ) {
    return null
  }

  const action = getNestedAction(setup)
  const requestedName =
    getString(setup.requestedName) ??
    getString(setup.requested_name) ??
    getString(setup.skillName) ??
    getString(setup.skill_name) ??
    getString(setup.name) ??
    getString(value.query) ??
    'Losi skill'
  const cardState =
    getString(setup.cardState) ??
    getString(setup.card_state) ??
    getString(setup.status) ??
    'pending_install'
  const kindBlob =
    `${getString(value.kind) ?? ''} ${getString(setup.kind) ?? ''}`.toLowerCase()
  const isReady =
    cardState === 'installed' ||
    cardState === 'enabled' ||
    kindBlob.includes('skill_ready')
  const description =
    getString(setup.description) ??
    (isReady
      ? 'This skill is installed and ready to use.'
      : 'Losi found this skill and prepared it for installation.')
  const title =
    getString(setup.title) ?? (isReady ? `${requestedName} ready` : `Install ${requestedName}`)

  return {
    actionLabel:
      getString(action.label) ??
      getString(setup.actionLabel) ??
      getString(setup.action_label) ??
      (isReady ? 'Use skill' : 'Install skill'),
    actionUrl:
      getString(action.url) ??
      getString(action.href) ??
      getString(setup.actionUrl) ??
      getString(setup.action_url),
    cardState: isReady ? 'installed' : cardState,
    description,
    enableUrl: isReady
      ? null
      : getString(setup.enableUrl) ??
        getString(setup.enable_url) ??
        getString(setup.reviewUrl) ??
        getString(setup.review_url),
    githubUrl:
      getString(setup.githubUrl) ??
      getString(setup.github_url) ??
      getString(setup.repositoryUrl) ??
      getString(setup.repository_url) ??
      getString(setup.sourceUrl) ??
      getString(setup.source_url),
    installUrl: isReady
      ? null
      : getString(setup.installUrl) ??
        getString(setup.install_url) ??
        getString(setup.setupUrl) ??
        getString(setup.setup_url) ??
        getString(action.installUrl) ??
        getString(action.install_url),
    integrationId:
      getString(setup.integrationId) ??
      getString(setup.integration_id) ??
      getString(rawSetup.integration_id),
    requestedName,
    title,
    warnings: isReady ? [] : getStringArray(setup.warnings)
  }
}

function openSetupUrl(url: string | null, label: string) {
  if (!url) {
    toast('Open Integrations to finish installing this skill.')
    return
  }

  const popup = window.open(url, 'losi-skill-install', 'height=760,width=720')

  if (!popup) {
    toast.error(`Allow popups to ${label.toLowerCase()}`)
  }
}

export function SkillSetupCardView({
  className,
  compact = false,
  setup
}: SkillSetupCardViewProps) {
  const [isEnabling, setIsEnabling] = useState(false)
  const isReady = setup.cardState === 'installed' || setup.cardState === 'enabled'
  const primaryUrl = isReady ? null : setup.actionUrl ?? setup.installUrl
  const needsReview = !isReady && isReviewState(setup.cardState)
  const warnings =
    needsReview && setup.warnings.length === 0
      ? ['Review the source before enabling this skill.']
      : setup.warnings
  const canEnable =
    !isReady &&
    Boolean(setup.enableUrl) &&
    setup.cardState !== 'installed' &&
    setup.cardState !== 'enabled'

  async function enableSkill() {
    if (!setup.enableUrl) {
      return
    }

    setIsEnabling(true)

    try {
      const response = await fetch(setup.enableUrl, {
        credentials: 'same-origin',
        method: 'POST'
      })
      const payload = await response.json().catch(() => null) as { message?: string } | null

      if (!response.ok) {
        throw new Error(payload?.message ?? 'Failed to enable skill')
      }

      toast.success(payload?.message ?? `${setup.requestedName} is ready to use`)
      window.dispatchEvent(new CustomEvent('losi-integrations-refresh'))
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to enable skill')
    } finally {
      setIsEnabling(false)
    }
  }

  return (
    <div
      className={cn(
        'border-border bg-background rounded-lg border p-4 shadow-sm',
        compact ? 'space-y-3' : 'space-y-4',
        className
      )}
    >
      <div className="flex min-w-0 items-start gap-3">
        <div className="bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-md">
          <BookOpenCheck className="size-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold">{setup.title}</h3>
            <Badge
              className={
                isReady
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : 'border-amber-200 bg-amber-50 text-amber-700'
              }
              variant="outline"
            >
              {setup.cardState.replace(/_/g, ' ')}
            </Badge>
          </div>
          <p className="text-muted-foreground mt-1 text-sm">{setup.description}</p>
        </div>
      </div>

      <div className="border-border bg-muted/30 rounded-md border px-3 py-2">
        <div className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
          {isReady ? 'Installed skill' : 'Requested skill'}
        </div>
        <div className="mt-0.5 truncate text-sm font-medium">{setup.requestedName}</div>
      </div>

      {!isReady && setup.githubUrl ? (
        <Button
          className="w-full justify-start"
          onClick={() => openSetupUrl(setup.githubUrl, 'Open GitHub')}
          type="button"
          variant="outline"
        >
          <Github className="size-4" />
          <span className="truncate">Open source repo</span>
        </Button>
      ) : null}

      {warnings.slice(0, 2).map((warning) => (
        <p className="text-xs text-amber-700" key={warning}>
          {warning}
        </p>
      ))}

      {isReady ? null : (
        <div className="grid gap-2 sm:grid-cols-2">
          {canEnable ? (
            <Button
              className="justify-center"
              disabled={isEnabling}
              onClick={enableSkill}
              type="button"
            >
              {isEnabling ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <BookOpenCheck className="size-4" />
              )}
              {needsReview ? 'Enable after review' : 'Enable skill'}
            </Button>
          ) : (
            <Button
              className="justify-center"
              onClick={() => openSetupUrl(primaryUrl, setup.actionLabel)}
              type="button"
            >
              <ExternalLink className="size-4" />
              {setup.actionLabel}
            </Button>
          )}
          <Button
            className="justify-center"
            onClick={() =>
              openSetupUrl(
                '/chat?sidebar=integrations&integrations=add&integration=skills',
                'Open Integrations'
              )
            }
            type="button"
            variant="outline"
          >
            Open Integrations
          </Button>
        </div>
      )}
    </div>
  )
}
